import React from "react";

import { ChevronDown, ChevronRight, Trash2 } from "lucide-react";

// One row for every upload in Uploads and versions (Sushant, 2026-11-04): a version
// and an upload that was never used share the same header (title, chip, key fact,
// date · person), the same three columns and the same actions line, so the two can't
// drift apart again.

export const Chev: React.FC<{ open: boolean }> = ({ open }) =>
  open ? (
    <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
  ) : (
    <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" aria-hidden />
  );

export const ROW_BTN =
  "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3 text-xs font-semibold text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50 disabled:opacity-50";

const DANGER_BTN =
  "inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-lg bg-rose-600 px-3 text-xs font-semibold text-white hover:bg-rose-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500/50 disabled:opacity-50";

// The same button, not supported yet: greyed, dashed, with the page's "Coming soon" tag.
const SOON_BTN =
  "inline-flex h-8 cursor-not-allowed items-center gap-1.5 whitespace-nowrap rounded-lg border border-dashed border-gray-300 bg-gray-50 px-3 text-xs font-semibold text-gray-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50";

export const Col: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="min-w-0 space-y-0.5">
    <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
      {title}
    </h4>
    {children}
  </div>
);

/**
 * Delete on a row. `soon`: deleting this kind of upload isn't built yet (a read,
 * confirmed, briefed or published version; Sushant, 2026-11-04), so the button is
 * shown greyed with "Coming soon" and does nothing.
 */
export const DeleteButton: React.FC<{
  soon?: boolean;
  onClick?: () => void;
  disabled?: boolean;
}> = ({ soon = false, onClick, disabled }) =>
  soon ? (
    <button
      type="button"
      aria-disabled="true"
      aria-label="Delete (coming soon)"
      data-testid="delete-coming-soon"
      className={SOON_BTN}
      onClick={e => e.preventDefault()}
    >
      <Trash2 className="h-3.5 w-3.5" aria-hidden />
      Delete
      <span className="rounded bg-gray-100 px-1 font-mono text-[9px] font-semibold uppercase tracking-wide text-gray-500">
        Coming soon
      </span>
    </button>
  ) : (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={ROW_BTN}
    >
      <Trash2 className="h-3.5 w-3.5" aria-hidden />
      Delete
    </button>
  );

/** The confirm before a delete: the page's confirm panel (as "Replace this draft"). */
export const DeleteConfirm: React.FC<{
  ask: string;
  busy?: boolean;
  onDelete: () => void;
  onCancel: () => void;
}> = ({ ask, busy, onDelete, onCancel }) => (
  <div
    role="group"
    aria-label="Delete this upload"
    data-testid="delete-unused-confirm"
    className="w-full max-w-xl rounded-md border border-rose-200 bg-white px-3 py-2"
  >
    <p className="text-[13px] text-gray-800">{ask}</p>
    <div className="mt-2 flex flex-wrap gap-2">
      <button
        type="button"
        disabled={busy}
        onClick={onDelete}
        className={DANGER_BTN}
      >
        <Trash2 className="h-3.5 w-3.5" aria-hidden />
        Delete
      </button>
      <button type="button" onClick={onCancel} className={ROW_BTN}>
        Cancel
      </button>
    </div>
  </div>
);

export interface UploadRowProps {
  /** data-testid and any data-* attributes for the row. */
  testId: string;
  attrs?: Record<string, string | number | boolean | undefined>;
  liRef?: React.Ref<HTMLLIElement>;
  /** Outlined in red: the problem the page was deep-linked to. */
  outlined?: boolean;
  open: boolean;
  onToggle: () => void;
  toggleLabel: string;
  title: string;
  chip: { label: string; cls: string };
  fact?: React.ReactNode;
  /** Beside the chip (e.g. "The problem"). */
  badge?: React.ReactNode;
  /** "3 Nov · Emily Chen"; left out in the compact panel. */
  when?: string | null;
  /** Buttons on the row's own line, open or not (Review, Undo, Open published). */
  headerActions?: React.ReactNode;
  /** Lines above the columns (a note, the full reason). */
  notes?: React.ReactNode;
  columns?: React.ReactNode;
  actions?: React.ReactNode;
}

/** Something to show: not null/false, and not a fragment of nothing. */
const shows = (node: React.ReactNode): boolean => {
  if (node === null || node === undefined || node === false || node === "")
    return false;
  if (React.isValidElement(node) && node.type === React.Fragment)
    return (
      React.Children.toArray(
        (node.props as { children?: React.ReactNode }).children
      ).length > 0
    );
  return true;
};

const UploadRow: React.FC<UploadRowProps> = ({
  testId,
  attrs = {},
  liRef,
  outlined = false,
  open,
  onToggle,
  toggleLabel,
  title,
  chip,
  fact,
  badge,
  when,
  headerActions,
  notes,
  columns,
  actions,
}) => {
  const data = Object.fromEntries(
    Object.entries(attrs).filter(([, v]) => v !== undefined && v !== false)
  );
  return (
    <li
      ref={liRef}
      data-testid={testId}
      data-focused={outlined || undefined}
      {...data}
      className={`rounded-lg border ${outlined ? "border-red-500 ring-2 ring-red-200" : "border-gray-200"}`}
    >
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-label={toggleLabel}
          className="flex min-w-0 flex-1 items-center gap-2 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[#974ae4]/50"
        >
          <Chev open={open} />
          <span className="whitespace-nowrap text-sm font-semibold text-gray-900">
            {title}
          </span>
          <span
            className={`whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-semibold ${chip.cls}`}
          >
            {chip.label}
          </span>
          {badge}
          {fact && (
            <span className="min-w-0 truncate text-[13px] text-gray-700">
              {fact}
            </span>
          )}
          {when && (
            <span className="ml-auto whitespace-nowrap text-xs text-gray-500">
              {when}
            </span>
          )}
        </button>
        {shows(headerActions) && headerActions}
      </div>
      {open && (shows(notes) || shows(columns) || shows(actions)) && (
        <div className="space-y-3 px-3 pb-3 pl-9">
          {shows(notes) && notes}
          {shows(columns) && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              {columns}
            </div>
          )}
          {shows(actions) && (
            <div className="flex flex-wrap gap-2">{actions}</div>
          )}
        </div>
      )}
    </li>
  );
};

export default UploadRow;
